import React, {
  ChangeEvent,
  FocusEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AsyncPaginate,
  AsyncPaginateProps,
  LoadOptions as LoadOptionsOriginal,
  ReduceOptions as ReduceOptionsOriginal,
  wrapMenuList,
} from 'react-select-async-paginate';
import {
  ActionMeta,
  components,
  DropdownIndicatorProps,
  GroupBase,
  OptionProps,
  SelectInstance,
  SingleValueProps,
  StylesConfig,
  Theme,
} from 'react-select';
import variables from 'scss/1-settings/colors.module.scss';
import '../SelectField/SelectField.scss';
import cx from 'classnames';
import InputFieldErrorMessage from 'components/InputFieldErrorMessage';
import { usePasswordManagerInputProps } from 'infrastructure/form/PasswordManagerAutofillContext';
import selectFieldCustomStyles from '../SelectField/SelectFieldCustomStyles';
import { SelectFieldAsyncOptionObject } from '../SelectFieldAsync/SelectFieldAsync';
import SelectFieldCustomClearIndicator from '../SelectFieldCustomClearIndicator/SelectFieldCustomClearIndicator';

import './SelectFieldAsyncPaginate.scss';

type PageInfo = {
  page: number;
};

type MenuSearchSelectProps = {
  renderSearchInMenu?: boolean;
  menuSearchPlaceholder?: string;
  menuSearchValue?: string;
  onMenuSearchChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onMenuSearchMouseDown?: (
    event: React.MouseEvent<HTMLDivElement | HTMLInputElement>
  ) => void;
  onMenuSearchFocus?: () => void;
  onMenuSearchBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onMenuSearchKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  menuSearchInputRef?: React.RefObject<HTMLInputElement>;
};

type SelectFieldAsyncPaginateSelectProps = MenuSearchSelectProps & {
  customOptionComponent?: React.ComponentType<any>;
  customSingleValueComponent?: React.ComponentType<any>;
  showActionLink?: boolean;
  passwordManagerInputProps?: ReturnType<typeof usePasswordManagerInputProps>;
};

export type LoadOptions<
  Option extends SelectFieldAsyncOptionObject = SelectFieldAsyncOptionObject,
  Additional extends object = PageInfo,
  Group extends GroupBase<Option> = GroupBase<Option>
> = (showAll: boolean) => LoadOptionsOriginal<Option, Group, Additional>;

export type ReduceOptions<
  Option extends SelectFieldAsyncOptionObject = SelectFieldAsyncOptionObject,
  Group extends GroupBase<Option> = GroupBase<Option>,
  Additional extends object = PageInfo
> = ReduceOptionsOriginal<Option, Group, Additional>;

export interface SelectFieldAsyncPaginateProps<
  Option extends SelectFieldAsyncOptionObject,
  Additional extends object = PageInfo,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> extends Omit<
    AsyncPaginateProps<Option, Group, Additional, IsMulti>,
    'loadOptions'
  > {
  id?: string;
  isTouched?: boolean;
  errors?: string;
  selectFieldId?: string;
  isLoading?: boolean;
  selectedValue?: AsyncPaginateProps<
    Option,
    Group,
    Additional,
    IsMulti
  >['value'];
  loadOptions: LoadOptions<Option, Additional, Group>;
  dataTestId?: string;
  disabledOption?: string;
  clearInputValueOnFocus?: boolean;
  clearCacheOnMenuClose?: boolean;
  focusedPlaceholder?: string;
  hideSelectedValueWhenMenuOpen?: boolean;
  renderSearchInMenu?: boolean;
  menuSearchPlaceholder?: string;
  variant?: 'field' | 'inline' | 'inlineLink';
  showDropdownIndicator?: boolean;
  dropdownIndicatorWidthPx?: number;
  indicatorsGapPx?: number;
  hideLoadingIndicator?: boolean;
  customOptionComponent?: React.ComponentType<
    OptionProps<Option, IsMulti, Group>
  >;
  customDropdownIndicatorComponent?: React.ComponentType<
    DropdownIndicatorProps<Option, IsMulti, Group>
  >;
  customSingleValueComponent?: React.ComponentType<
    SingleValueProps<Option, IsMulti, Group>
  >;
  showActionLink?: boolean;
}

const Input = ({ selectProps, value, ...inputProps }: any) => {
  const { passwordManagerInputProps = {} } = (selectProps ||
    {}) as SelectFieldAsyncPaginateSelectProps;

  return (
    <components.Input
      {...inputProps}
      autoComplete={undefined}
      selectProps={selectProps}
      value={selectProps?.renderSearchInMenu ? '' : value}
      isHidden={Boolean(selectProps?.renderSearchInMenu)}
      {...passwordManagerInputProps}
    />
  );
};

const MenuListBase = (menuListProps: any) => {
  const { children, selectProps, ...restMenuListProps } = menuListProps;
  const {
    renderSearchInMenu,
    menuSearchPlaceholder = 'Search...',
    menuSearchValue = '',
    onMenuSearchChange,
    onMenuSearchMouseDown,
    onMenuSearchFocus,
    onMenuSearchBlur,
    onMenuSearchKeyDown,
    menuSearchInputRef,
    passwordManagerInputProps = {},
  } = (selectProps || {}) as SelectFieldAsyncPaginateSelectProps;

  return (
    <components.MenuList {...restMenuListProps} selectProps={selectProps}>
      {renderSearchInMenu ? (
        <div
          className="select-field-async-paginate__menu-search"
          onMouseDown={onMenuSearchMouseDown}
          role="presentation"
        >
          <input
            {...passwordManagerInputProps}
            type="text"
            ref={menuSearchInputRef}
            value={menuSearchValue}
            onChange={onMenuSearchChange}
            onMouseDown={onMenuSearchMouseDown}
            onFocus={onMenuSearchFocus}
            onBlur={onMenuSearchBlur}
            onKeyDown={onMenuSearchKeyDown}
            placeholder={menuSearchPlaceholder}
            aria-label={menuSearchPlaceholder}
          />
        </div>
      ) : null}
      {children}
    </components.MenuList>
  );
};

const MenuList = wrapMenuList(MenuListBase);

const NullLoadingIndicator = () => null;

const OptionComponent = (optionProps: any) => {
  const { innerProps, data, isDisabled, selectOption, selectProps } =
    optionProps;
  const {
    customOptionComponent: CustomOptionComponent,
    renderSearchInMenu,
    showActionLink,
  } = (selectProps || {}) as SelectFieldAsyncPaginateSelectProps;
  const BaseOptionComponent = CustomOptionComponent || components.Option;
  const optionData = showActionLink ? { ...data, showActionLink } : data;
  const resolvedInnerProps = renderSearchInMenu
    ? {
        ...innerProps,
        onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => {
          event.stopPropagation();
          event.preventDefault();

          if (!isDisabled) {
            selectOption?.(data);
          }
        },
      }
    : innerProps;

  return (
    <BaseOptionComponent
      {...optionProps}
      data={optionData}
      innerProps={resolvedInnerProps}
    />
  );
};

const SingleValueComponent = (singleValueProps: any) => {
  const { data, selectProps } = singleValueProps;
  const {
    customSingleValueComponent: CustomSingleValueComponent,
    showActionLink,
  } = (selectProps || {}) as SelectFieldAsyncPaginateSelectProps;
  const BaseSingleValueComponent =
    CustomSingleValueComponent || components.SingleValue;
  const singleValueData = showActionLink ? { ...data, showActionLink } : data;

  return (
    <BaseSingleValueComponent {...singleValueProps} data={singleValueData} />
  );
};

const ValueContainerComponent = (valueContainerProps: any) => {
  const { children, getValue, isDisabled, isMulti, selectProps } =
    valueContainerProps;
  const { renderSearchInMenu, customSingleValueComponent, showActionLink } =
    (selectProps || {}) as SelectFieldAsyncPaginateSelectProps;
  const selectedOption = getValue?.()?.[0];
  const shouldPersistSelectedValue = Boolean(
    renderSearchInMenu &&
      !isMulti &&
      selectProps?.controlShouldRenderValue &&
      selectProps?.inputValue &&
      selectedOption
  );
  const BaseSingleValueComponent =
    customSingleValueComponent || components.SingleValue;
  const singleValueData =
    selectedOption && showActionLink
      ? { ...selectedOption, showActionLink }
      : selectedOption;

  return (
    <components.ValueContainer {...valueContainerProps}>
      {shouldPersistSelectedValue ? (
        <BaseSingleValueComponent
          {...valueContainerProps}
          data={singleValueData}
          isDisabled={isDisabled}
        >
          {selectedOption.label}
        </BaseSingleValueComponent>
      ) : null}
      {children}
    </components.ValueContainer>
  );
};

const reactSelectTheme = (theme: Theme) => ({
  ...theme,
  borderRadius: 0.2,
  borderColor: 'border-color',
  colors: {
    ...theme.colors,
    primary: variables.success,
    primary75: variables.success75,
    primary50: variables.success50,
    primary25: variables.success25,
    danger: variables.danger,
    dangerLight: variables.dangerLighter,
  },
});

const customStyles: StylesConfig = {
  input: provided => ({
    ...provided,
    input: {
      height: '100% !important',
      visibility: 'visible',
    },
  }),
  option: (styles, { isDisabled }) => ({
    ...styles,
    backgroundColor: isDisabled ? variables.gray100 : 'inherit',
  }),
  groupHeading: styles => ({
    ...styles,
    textTransform: 'none',
    fontSize: '0.7rem',
    paddingBottom: '4px',
  }),
  menuPortal: styles => ({
    ...styles,
    zIndex: 12,
  }),
};

const SelectFieldAsyncPaginate = <
  Option extends SelectFieldAsyncOptionObject,
  Additional extends object,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  props: SelectFieldAsyncPaginateProps<Option, Additional, IsMulti, Group> & {
    selectedOptionRedirectUrl?: string;
  }
) => {
  const passwordManagerInputProps = usePasswordManagerInputProps();
  const {
    isTouched,
    errors,
    selectFieldId,
    isClearable = true,
    isMulti,
    classNamePrefix,
    className,
    controlShouldRenderValue = false,
    value,
    loadOptions,
    onChange,
    onBlur,
    placeholder,
    dataTestId = '',
    disabledOption,
    clearInputValueOnFocus = false,
    selectedOptionRedirectUrl: redirectUrl,
    customOptionComponent,
    customSingleValueComponent,
    showActionLink,
    clearCacheOnMenuClose = false,
    focusedPlaceholder,
    hideSelectedValueWhenMenuOpen = false,
    renderSearchInMenu = false,
    menuSearchPlaceholder = 'Search...',
    variant = 'field',
    showDropdownIndicator = false,
    dropdownIndicatorWidthPx = 15,
    indicatorsGapPx = 2,
    hideLoadingIndicator = false,
    customDropdownIndicatorComponent,
    ...asyncPaginateProps
  } = props;
  const [inputValue, setInputValue] = useState(
    controlShouldRenderValue ? '' : (value as any)?.label
  );
  const [menuSearchValue, setMenuSearchValue] = useState('');
  const [selectedValues, setSelectedValues] = useState<any>(value);
  useEffect(() => {
    setInputValue(controlShouldRenderValue ? '' : (value as any)?.label);
    setSelectedValues(value);
  }, [controlShouldRenderValue, value]);
  const [showAllOptions, setShowAllOptions] = useState(false);
  const [cacheBuster, setCacheBuster] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<SelectInstance<Option, IsMulti, Group>>(null);
  const menuSearchInputRef = useRef<HTMLInputElement>(null);
  const shouldIgnoreNextMenuCloseRef = useRef(false);
  const shouldIgnoreNextInternalBlurRef = useRef(false);
  const shouldSuppressNextMenuOpenRef = useRef(false);

  const shouldHideSelectedValue = hideSelectedValueWhenMenuOpen && isMenuOpen;
  const shouldRenderValue =
    controlShouldRenderValue && !shouldHideSelectedValue;
  const shouldControlMenuState =
    renderSearchInMenu || hideSelectedValueWhenMenuOpen;
  const currentPlaceholder =
    shouldHideSelectedValue && focusedPlaceholder
      ? focusedPlaceholder
      : placeholder;
  const isInline = variant.startsWith('inline');
  const resolvedClassNamePrefix =
    classNamePrefix || (isInline ? 'inlineRs' : 'rs');

  const isTargetInsideSelect = useCallback((target: EventTarget | null) => {
    const node = target as Node | null;

    if (!node) {
      return false;
    }

    return Boolean(
      containerRef.current?.contains(node) ||
        selectRef.current?.controlRef?.contains(node) ||
        selectRef.current?.menuListRef?.contains(node)
    );
  }, []);

  const handleFocus = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (clearInputValueOnFocus) {
        setInputValue('');
      }
      event?.target?.select();
      setShowAllOptions(true);
    },
    [clearInputValueOnFocus]
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      const shouldIgnoreBlur = shouldIgnoreNextInternalBlurRef.current;

      shouldIgnoreNextInternalBlurRef.current = false;
      shouldSuppressNextMenuOpenRef.current = false;

      if (shouldIgnoreBlur) {
        return;
      }

      if (
        renderSearchInMenu &&
        event.relatedTarget &&
        (event.relatedTarget === menuSearchInputRef.current ||
          isTargetInsideSelect(event.relatedTarget))
      ) {
        return;
      }

      setIsMenuOpen(false);
      onBlur?.(event);
    },
    [isTargetInsideSelect, onBlur, renderSearchInMenu]
  );

  const onInputChange = useCallback(
    (input: string, { action }: { action: string }) => {
      if (renderSearchInMenu) {
        return input;
      }

      // Closing the menu also clears search when the input keeps focus.
      // Single selects restore the selected value instead of the search.
      if (action === 'input-blur' || action === 'menu-close') {
        setInputValue(
          isMulti || controlShouldRenderValue ? '' : selectedValues?.label || ''
        );
      } else if (action === 'input-change') {
        // onInputChange => update inputValue
        setInputValue(input);
      }
      setShowAllOptions(false);
      return input;
    },
    [controlShouldRenderValue, isMulti, renderSearchInMenu, selectedValues]
  );

  const handleMenuSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value;

      setMenuSearchValue(nextValue);
      setShowAllOptions(nextValue.length === 0);
    },
    []
  );

  const handleMenuSearchKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      event.stopPropagation();
    },
    []
  );

  const handleMenuSearchMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement | HTMLInputElement>) => {
      shouldIgnoreNextMenuCloseRef.current = true;
      event.stopPropagation();

      window.setTimeout(() => {
        menuSearchInputRef.current?.focus();
      }, 0);
    },
    []
  );

  const handleMenuSearchFocus = useCallback(() => {
    setIsMenuOpen(true);
    setTimeout(() => {
      shouldIgnoreNextMenuCloseRef.current = false;
    }, 0);
  }, []);

  const handleChange = useCallback(
    (option: any, actionMeta: ActionMeta<Option>) => {
      if (actionMeta.action === 'pop-value') return;
      onChange?.(option, actionMeta);
      setInputValue(controlShouldRenderValue ? '' : option?.label || '');
      setSelectedValues(option);
    },
    [controlShouldRenderValue, onChange]
  );

  const handleClearIndicatorMouseDown = useCallback(() => {
    if (isMenuOpen || !asyncPaginateProps.openMenuOnFocus) {
      return;
    }

    shouldSuppressNextMenuOpenRef.current = true;
  }, [asyncPaginateProps.openMenuOnFocus, isMenuOpen]);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    setShowAllOptions(true);
    setMenuSearchValue('');
    shouldIgnoreNextInternalBlurRef.current = false;
    if (renderSearchInMenu) {
      setInputValue(
        controlShouldRenderValue ? '' : selectedValues?.label || ''
      );
    }
    if (clearCacheOnMenuClose) {
      setCacheBuster(current => current + 1);
    }
    asyncPaginateProps.onMenuClose?.();
  }, [
    asyncPaginateProps,
    clearCacheOnMenuClose,
    controlShouldRenderValue,
    renderSearchInMenu,
    selectedValues,
  ]);

  const handleMenuSearchBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      const shouldIgnoreBlur = shouldIgnoreNextInternalBlurRef.current;

      shouldIgnoreNextInternalBlurRef.current = false;
      shouldIgnoreNextMenuCloseRef.current = false;

      if (shouldIgnoreBlur) {
        return;
      }

      const nextFocusTarget = event.relatedTarget;
      if (nextFocusTarget && isTargetInsideSelect(nextFocusTarget)) {
        return;
      }

      closeMenu();
    },
    [closeMenu, isTargetInsideSelect]
  );

  const asyncPaginateInputValue = renderSearchInMenu
    ? menuSearchValue
    : inputValue;

  useEffect(() => {
    if (!renderSearchInMenu || !isMenuOpen) {
      return undefined;
    }

    const focusTimer = window.setTimeout(() => {
      menuSearchInputRef.current?.focus();
    }, 0);

    const handleDocumentMouseDownCapture = (event: MouseEvent) => {
      const { activeElement } = document;
      const selectInput = selectRef.current?.inputRef;

      shouldIgnoreNextInternalBlurRef.current = Boolean(
        isTargetInsideSelect(event.target) &&
          activeElement &&
          (activeElement === menuSearchInputRef.current ||
            activeElement === selectInput) &&
          event.target !== activeElement
      );
    };

    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (!isTargetInsideSelect(event.target)) {
        closeMenu();
      }
    };

    document.addEventListener(
      'mousedown',
      handleDocumentMouseDownCapture,
      true
    );
    document.addEventListener('mousedown', handleDocumentMouseDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener(
        'mousedown',
        handleDocumentMouseDownCapture,
        true
      );
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [closeMenu, isMenuOpen, isTargetInsideSelect, renderSearchInMenu]);

  const memoizedComponents = useMemo(
    () => ({
      Input,
      ClearIndicator: SelectFieldCustomClearIndicator,
      LoadingIndicator: hideLoadingIndicator
        ? NullLoadingIndicator
        : components.LoadingIndicator,
      DropdownIndicator:
        customDropdownIndicatorComponent || components.DropdownIndicator,
      Option: OptionComponent,
      SingleValue: SingleValueComponent,
      ValueContainer: renderSearchInMenu
        ? ValueContainerComponent
        : components.ValueContainer,
      ...(renderSearchInMenu
        ? {
            MenuList,
          }
        : {}),
    }),
    [customDropdownIndicatorComponent, hideLoadingIndicator, renderSearchInMenu]
  );

  return (
    <div
      ref={containerRef}
      data-testid={dataTestId || selectFieldId}
      className={cx('SelectField', 'select-field-async-paginate', {
        inline: isInline,
      })}
    >
      <AsyncPaginate
        {...asyncPaginateProps}
        {...({
          renderSearchInMenu,
          menuSearchPlaceholder,
          menuSearchValue,
          customOptionComponent,
          customSingleValueComponent,
          showActionLink,
          onClearIndicatorMouseDown: handleClearIndicatorMouseDown,
          redirectUrl,
          onMenuSearchChange: handleMenuSearchChange,
          onMenuSearchMouseDown: handleMenuSearchMouseDown,
          onMenuSearchFocus: handleMenuSearchFocus,
          onMenuSearchBlur: handleMenuSearchBlur,
          onMenuSearchKeyDown: handleMenuSearchKeyDown,
          menuSearchInputRef,
          passwordManagerInputProps,
        } as any)}
        theme={reactSelectTheme}
        // casting to `any` so we don't have to deal with generic parameters.
        // they are useless here anyway - we're just setting react-select styles.
        styles={
          isInline
            ? (selectFieldCustomStyles(
                variant === 'inlineLink',
                Boolean(isMulti),
                isClearable,
                showDropdownIndicator,
                dropdownIndicatorWidthPx,
                indicatorsGapPx
              ) as any)
            : (customStyles as any)
        }
        value={selectedValues}
        selectRef={selectRef}
        components={memoizedComponents}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onMenuOpen={() => {
          if (shouldSuppressNextMenuOpenRef.current) {
            shouldSuppressNextMenuOpenRef.current = false;
            return;
          }

          setIsMenuOpen(true);
          if (renderSearchInMenu) {
            setMenuSearchValue('');
          }
          setShowAllOptions(true);
          asyncPaginateProps.onMenuOpen?.();
        }}
        onInputChange={onInputChange}
        onChange={handleChange}
        inputValue={asyncPaginateInputValue}
        isMulti={isMulti}
        isClearable={isClearable}
        classNamePrefix={resolvedClassNamePrefix}
        className={cx(className, {
          alternative: isInline,
        })}
        loadOptions={loadOptions(showAllOptions)}
        controlShouldRenderValue={shouldRenderValue}
        placeholder={currentPlaceholder}
        onMenuClose={() => {
          if (renderSearchInMenu && shouldIgnoreNextMenuCloseRef.current) {
            return;
          }
          closeMenu();
        }}
        isOptionDisabled={(option: Option) => option.value === disabledOption}
        cacheUniqs={[
          ...(asyncPaginateProps.cacheUniqs || []),
          ...(clearCacheOnMenuClose ? [cacheBuster] : []),
          ...(renderSearchInMenu ? [menuSearchValue] : []),
        ]}
        menuIsOpen={shouldControlMenuState ? isMenuOpen : undefined}
      />
      {isTouched && errors ? (
        <InputFieldErrorMessage
          dataTestId={`${dataTestId || selectFieldId}__selectInputFormFeedback`}
        >
          {errors}
        </InputFieldErrorMessage>
      ) : null}
    </div>
  );
};

export default SelectFieldAsyncPaginate;
